import clsx from "clsx";
import debounce from "lodash/debounce";
import React, { useEffect, useRef, useState } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH } from "common/constants";
import Loader from "components/Loader";
import MediaPlayButton from "components/MediaPlayButton";
import CodedPPS from "components/PastPerformances/CodedPPs";
import WorkoutDetails from "components/VetActivity/Workouts/WorkoutDetails";
import { useRacehorse360Api } from "hooks/api";
import { useInfiniteScroll } from "hooks/useInfiniteScroll";
import { SortOrder } from "interfaces/SortOrder";
import { transformViewDate } from "utils/date-utils";
import { toTitleCase } from "utils/horse";
import { IScrollPositions } from "../../HorseInfoContainer";
import HorseInfoTable, {
  IHorseInfoTableData
} from "../HorseInfoTable/HorseInfoTable";
import HorseInfoTabs from "../HorseInfoTabs";
import { checkSelectedTab, EExamInfoTabs, insertCharToString } from "../helper";

import useStyles from "./styles";

interface IProps {
  horse: racehorse360.IHorse;
  currentTab?: EExamInfoTabs;
  onSetCurrentTab?: (value: EExamInfoTabs) => void;
  onSetTabPosition?: (value: IScrollPositions) => void;
  tabPositions?: IScrollPositions;
}

interface IScratchTableContent extends IHorseInfoTableData {
  Track: string;
  "Race Number": number;
  Reason: string;
}

interface IScratch {
  id: string;
  date: string;
  tableContent: IScratchTableContent;
}

const tabs: EExamInfoTabs[] = [
  EExamInfoTabs.PPS,
  EExamInfoTabs.WORKS,
  EExamInfoTabs.SCRATCHES
];

const WORKOUT_MAX_RESULTS = 15;
const RACE_HISTORY_MAX_RESULTS = 10;

const Performance = (props: IProps) => {
  const { horse, currentTab, onSetCurrentTab, onSetTabPosition, tabPositions } =
    props;
  const classes = useStyles();
  const tabContainerRef = useRef<HTMLDivElement>(null);

  const [selectedTab, setSelectedTab] = useState<EExamInfoTabs>(
    currentTab || tabs[0]
  );

  const { isPPS, isWorks, isScratches } = checkSelectedTab(selectedTab);
  const { useInfiniteListRaceHistories, useInfiniteListWorkouts } =
    useRacehorse360Api();

  const {
    isLoading: isLoadingRaceHistories,
    data: raceHistoriesData,
    fetchNextPage: fetchNextPageRaceHistories,
    isFetchingNextPage: isFetchingNextPageRaceHistories,
    hasNextPage: hasNextPageRaceHistories
  } = useInfiniteListRaceHistories(
    {
      query: {
        horse: horse.id,
        isScratched: isScratches ? { value: isScratches } : null
      },
      pagingOptions: {
        maxResults: RACE_HISTORY_MAX_RESULTS
      },
      getOptions: {
        select: [
          "facility.name",
          "race",
          "id",
          "comment",
          "date",
          "facility.code",
          "raceNumber",
          "isScratched",
          "scratchReason",
          "positionAtPointOfCall1",
          "positionAtPointOfCall2",
          "positionAtPointOfCall3",
          "positionAtPointOfCall4",
          "positionAtPointOfCall5",
          "positionAtFinish",
          "lengthAheadAtPointOfCall1",
          "lengthAheadAtPointOfCall2",
          "lengthAheadAtPointOfCall3",
          "lengthAheadAtPointOfCall4",
          "lengthAheadAtPointOfCall5",
          "lengthAheadAtFinish",
          "lengthBehindAtPointOfCall1",
          "lengthBehindAtPointOfCall2",
          "lengthBehindAtPointOfCall3",
          "lengthBehindAtPointOfCall4",
          "lengthBehindAtPointOfCall5",
          "lengthBehindAtFinish",
          "medication",
          "postPosition",
          "race.id",
          "race.surface",
          "race.trackCondition",
          "race.courseType",
          "race.distance",
          "race.distanceUnit",
          "race.ageRestriction",
          "race.genderRestriction",
          "race.raceType",
          "race.raceNameAbbreviated",
          "race.raceConditions",
          "race.raceName",
          "race.numberOfRunners",
          "race.aboutDistanceIndicator",
          "race.fraction1",
          "race.fraction2",
          "race.fraction3",
          "race.fraction4",
          "race.fraction5",
          "race.surface",
          "jockey.firstName",
          "jockey.lastName",
          "weightCarried",
          "medication",
          "equipment",
          "odds",
          "newOwner.firstName",
          "newOwner.lastName",
          "newTrainer.firstName",
          "newTrainer.middleName",
          "newTrainer.lastName",
          "isVoid",
          "voidReason"
        ],
        orderBy: [`date ${SortOrder.DESC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );

  const raceHistories = raceHistoriesData?.pages.flat() || [];

  const preparedScratches: IScratch[] = raceHistories.map(
    (history: racehorse360.IRaceHistory) => {
      return {
        id: history.id,
        date: history.date
          ? insertCharToString(
              transformViewDate(history.date, null, "PP"),
              ".",
              3
            )
          : DASH,
        tableContent: {
          Track: toTitleCase(history.facility?.name?.toLowerCase()) || DASH,
          "Race Number": history.raceNumber,
          Reason: history.scratchReason || DASH
        }
      };
    }
  );

  const {
    isLoading: isLoadingWorkouts,
    data: workoutsData,
    fetchNextPage: fetchNextPageWorkouts,
    isFetchingNextPage: isFetchingNextPageWorkouts,
    hasNextPage: hasNextPageWorkouts
  } = useInfiniteListWorkouts(
    {
      query: {
        horseIds: [horse.id]
      },
      pagingOptions: {
        maxResults: WORKOUT_MAX_RESULTS
      },
      getOptions: {
        select: [
          "id",
          "date",
          "facility.name",
          "ranking",
          "gateIndicator",
          "dogIndicator",
          "distance",
          "distanceUnit",
          "trackCondition",
          "workoutType",
          "timing",
          "numberInRankingGroup"
        ],
        orderBy: [`date ${SortOrder.DESC}`]
      }
    },
    {
      onError: error => console.error(error)
    }
  );

  useInfiniteScroll(
    tabContainerRef,
    isWorks ? fetchNextPageWorkouts : fetchNextPageRaceHistories,
    isWorks ? hasNextPageWorkouts : hasNextPageRaceHistories,
    isWorks ? isFetchingNextPageWorkouts : isFetchingNextPageRaceHistories,
    true,
    true
  );

  const workouts = workoutsData?.pages.flat() || [];

  const handleOpenVideoWorkout = (
    event: React.MouseEvent,
    workout: racehorse360.IWorkout
  ) => {
    return;
  };

  const handleOpenVideoRace = (
    event: React.MouseEvent,
    race: racehorse360.IRaceHistory
  ) => {
    return;
  };

  const handleChangeTab = (value: EExamInfoTabs) => {
    setSelectedTab(value);
    onSetCurrentTab && onSetCurrentTab(value);
  };

  const renderNoResults = (text: string) => {
    return <div className={classes.noResults}>{text}</div>;
  };

  const renderPPsTabContent = () => {
    if (!raceHistories.length) {
      return <div className={classes.noResults}>No Past Performances</div>;
    }

    return raceHistories.map((race: racehorse360.IWorkout) => {
      return (
        <div key={race.id} className={classes.ppsEntry}>
          <div>
            <CodedPPS raceHistory={race} />
          </div>
          <MediaPlayButton
            className={clsx(classes.mediaPlayButton, classes.hidden)}
            entity={race}
            onOpenMedia={handleOpenVideoRace}
            isDisabled={!race["replay"]}
          />
        </div>
      );
    });
  };

  const renderWorksTabContent = () => {
    if (!workouts.length) {
      return renderNoResults("No Workouts");
    }

    return workouts.map((workout: racehorse360.IWorkout) => {
      return (
        <div key={workout.id} className={classes.entry}>
          <div className={classes.workoutSummary}>
            <span className={classes.headerDate}>
              {workout.date
                ? insertCharToString(
                    transformViewDate(workout.date, null, "PP"),
                    ".",
                    3
                  )
                : DASH}
            </span>
            <span className={classes.workoutFacility}>
              {workout.facility?.name?.toLowerCase() || DASH}
            </span>
          </div>
          <div className={classes.workout}>
            <div className={classes.workoutDetails}>
              <WorkoutDetails workout={workout} />
            </div>
            <MediaPlayButton
              className={classes.hidden}
              entity={workout}
              onOpenMedia={handleOpenVideoWorkout}
              isDisabled={!workout["replay"]}
            />
          </div>
        </div>
      );
    });
  };

  const renderScratchesTabContent = () => {
    if (!preparedScratches.length) {
      return renderNoResults("No Scratches");
    }

    return preparedScratches.map((scratch: IScratch) => {
      return (
        <div key={scratch.id} className={classes.entry}>
          <div className={classes.headerDate}>{scratch.date}</div>
          <HorseInfoTable data={scratch.tableContent} rowHeight={32} />
        </div>
      );
    });
  };

  const handleScrollTabContainer = () => {
    const scrollTop = tabContainerRef.current.scrollTop;

    isPPS && onSetTabPosition({ ...tabPositions, ppsTabPosition: scrollTop });
    isWorks &&
      onSetTabPosition({ ...tabPositions, worksTabPosition: scrollTop });
    isScratches &&
      onSetTabPosition({ ...tabPositions, scratchesTabPosition: scrollTop });
  };

  const isLoading = isLoadingRaceHistories || isLoadingWorkouts;
  const isFetchingNextPage =
    isFetchingNextPageWorkouts || isFetchingNextPageRaceHistories;

  useEffect(() => {
    if (isPPS) {
      tabContainerRef.current.scrollTop = tabPositions?.ppsTabPosition || 0;
    } else if (isWorks) {
      tabContainerRef.current.scrollTop = tabPositions?.worksTabPosition || 0;
    } else {
      tabContainerRef.current.scrollTop =
        tabPositions?.scratchesTabPosition || 0;
    }
  }, [selectedTab]);

  return (
    <>
      <HorseInfoTabs
        data={tabs}
        selectedTab={selectedTab}
        onSetSelectedTab={handleChangeTab}
      />
      <div className={classes.scrollableContainer}>
        <div
          className={classes.contentContainer}
          ref={tabContainerRef}
          onScroll={debounce(handleScrollTabContainer, 500)}
        >
          {isLoading && <Loader />}
          {isPPS && renderPPsTabContent()}
          {isWorks && renderWorksTabContent()}
          {isScratches && renderScratchesTabContent()}
        </div>
        {isFetchingNextPage && (
          <Loader className={classes.nextPageFetchingLoader} />
        )}
      </div>
    </>
  );
};

export default Performance;
